import React, { useMemo } from 'react';
import { Form, FormInstance, Select } from 'antd';
import { MessageType, GROUP_MESSAGING_TYPES } from './_hooks';
import { SpearfishableWorkersByShiftGapQueryCgResponse } from '@common/graphql/schema';
import { EnumType } from '@common/graphql/queries/enum';
import { FontWeight } from '@common/tokens';

export function MessageTypeSelector({
  groupMessagingEnabled,
  homeUnitSpearfishableWorkerData,
  preferenceMatchSpearfishableWorkerData,
  form,
  selectedMessageType,
  setSelectedMessageType,
  setWorkerIds,
  setGroupIds,
  messageTypeData,
}: {
  groupMessagingEnabled: boolean;
  homeUnitSpearfishableWorkerData:
    | SpearfishableWorkersByShiftGapQueryCgResponse
    | undefined;
  preferenceMatchSpearfishableWorkerData:
    | SpearfishableWorkersByShiftGapQueryCgResponse
    | undefined;
  form: FormInstance;
  selectedMessageType: MessageType;
  setSelectedMessageType: (messageType: MessageType) => void;
  setWorkerIds: (workerIds: string[]) => void;
  setGroupIds: (groupIds: string[]) => void;
  messageTypeData: EnumType[];
}) {
  const spearfishableHomeUnitWorkers = useMemo(
    () =>
      homeUnitSpearfishableWorkerData?.spearfishableWorkersByShiftGap?.filter(
        (worker) => worker.spearfishable
      ) || [],
    [homeUnitSpearfishableWorkerData]
  );
  const spearfishablePreferenceMatchWorkers = useMemo(
    () =>
      preferenceMatchSpearfishableWorkerData?.spearfishableWorkersByShiftGap?.filter(
        (worker) => worker.spearfishable
      ) || [],
    [preferenceMatchSpearfishableWorkerData]
  );

  const onMessageTypeChange = (messageType: MessageType) => {
    form.setFieldsValue({ messageType });
    form.resetFields(['workers', 'groups']);

    setSelectedMessageType(messageType);
    setWorkerIds([]);
    setGroupIds([]);

    if (messageType === 'home_unit') {
      setWorkerIds(
        spearfishableHomeUnitWorkers?.map((sw) => sw.worker.id) || []
      );
    }
    if (messageType === 'preference_match') {
      setWorkerIds(
        spearfishablePreferenceMatchWorkers?.map((sw) => sw.worker.id) || []
      );
    }
  };

  const spearfishingMessageTypes = useMemo(() => {
    const workerCountPill = (workerCount: number) => (
      <span className="count-pill">{workerCount}</span>
    );

    return messageTypeData?.map((enumValue) => {
      return {
        label: (
          <>
            {enumValue.description}
            {enumValue.name === 'home_unit' &&
              workerCountPill(spearfishableHomeUnitWorkers.length)}
            {enumValue.name === 'preference_match' &&
              workerCountPill(spearfishablePreferenceMatchWorkers.length)}
          </>
        ),
        value: enumValue.name,
        hidden:
          GROUP_MESSAGING_TYPES.includes(enumValue.name) &&
          !groupMessagingEnabled,
      };
    });
  }, [
    groupMessagingEnabled,
    messageTypeData,
    spearfishableHomeUnitWorkers.length,
    spearfishablePreferenceMatchWorkers.length,
  ]);

  return (
    <>
      <Form.Item
        label="Message Type"
        name="messageType"
        className="wk-message-type-selector-form-item"
        required
      >
        <Select
          onChange={(e) => {
            onMessageTypeChange(e as MessageType);
          }}
          className="wk-message-type-selector-select-input"
          value={selectedMessageType}
          placeholder="Select a message type"
          options={spearfishingMessageTypes}
        />
      </Form.Item>
      <style jsx>
        {`
          :global(.wk-message-type-selector-form-item) {
            font-weight: ${FontWeight.medium};
            margin-bottom: 24px;
          }

          :global(.wk-message-type-selector-select-input) {
            width: 100%;
          }
        `}
      </style>
    </>
  );
}
